import React, { JSXElementConstructor, ReactElement, useCallback, useMemo, useState } from 'react';
import { AuditLogs } from '../../../primary-record-types';
import { useAuditLogs } from '../../../hooks/api/useAuditLogs';
import LogSearch, { applySearchFilter } from './LogSearch';
import Stack, { contentStackSpacing } from '../../v2BaseComponents/Stack';
import { AnimateLoadedContent } from '../../v2BaseComponents/Loader';
import HeaderContainer from '../../v2BaseComponents/HeaderContainer';
import { ListRenderItem, Platform, View, StyleSheet } from 'react-native';
import { AuditLogLogCard } from './LogCard';
import { mainContentPadding } from '../../v2BaseComponents/MainContentContainer';
import { getSpacing } from '../../../utils/spacing';
import EmptyStateHint, { EmptyStateVariant } from '../EmptyStateHint';
import { ProfileSelectionState } from './types';
import { AuditLogListItem } from '../../../primary-record-types/apiTypes/auditLogService';
import { v4 as uuid } from 'uuid';
import { PRFlatList } from '../Scroll';

const homebaseAuditLogTypes: AuditLogs.AuditLogTypes[] = [
    'user-profile-view',
    'medical-profile-creation',
    'medical-profile-deletion',
    'collaborator-add',
    'collaborator-remove',
    'collaborator-invite',
    'collaborator-invite-accept',
    'collaborator-invite-decline',
    'care-team-member-collaborator-remove',
    'care-team-member-collaborator-add',
    'resource-creation',
    'resource-update',
    'resource-delete',
    'note-creation',
    'note-move',
    'note-comment',
    '1up-integration-add',
    '1up-integration-refresh',
    '1up-integration-remove',
    'fasten-integration-add',
    'fasten-integration-refresh',
    'fasten-integration-remove',
    'medical-profile-ownership-transfer',
    'shared-report-creation',
    'shared-report-deletion',
    'shared-report-viewed',
    'portal-refresh-completion',
    'profile-message',
    'organization-collaborator-add',
    'organization-collaborator-remove',
    'medical-profile-file-upload',
    'medical-profile-file-delete',
    'organization-invoice-creation',
    'code-status-directive-creation',
    'code-status-directive-update',
    'poa-directive-creation',
    'poa-directive-update',
];

export type LogPressArgs = {
    auditLogId: string;
    medicalProfileId: string | null;
};

export type LogListOwnProps = {
    disableSelection?: boolean;
    onLogPress: (args: LogPressArgs) => void;
    getPreLogListCards?: (profileIds: string[]) => (ReactElement<any, string | JSXElementConstructor<any>> | null)[];
    auditLogTypes?: AuditLogs.AuditLogTypes[];
    searchTextPlaceholder?: string;
    flat?: boolean;
    searchEmptyStateVariant?: EmptyStateVariant;
    noDataEmptyStateVariant?: EmptyStateVariant;
} & ProfileSelectionState;

export type LogListProps = LogListOwnProps;

type PreLogCard = {
    type: 'pre-log-card';
    card: ReactElement<any, string | JSXElementConstructor<any>> | null;
    key: string;
};

const isPreLogCard = (card: PreLogCard | AuditLogListItem): card is PreLogCard => card.type === 'pre-log-card';

const FlatListAuditLogItem: React.FC<{
    auditLog: PreLogCard | AuditLogListItem;
    onLogPress: (args: LogPressArgs) => void;
    flat?: boolean;
}> = React.memo(({ auditLog, onLogPress, flat }) =>
    isPreLogCard(auditLog) ? (
        auditLog.card
    ) : (
        <AuditLogLogCard
            log={auditLog}
            key={auditLog.auditLogId}
            onPress={() => {
                onLogPress({
                    auditLogId: auditLog.auditLogId,
                    medicalProfileId: auditLog.medicalProfileId || null,
                });
            }}
            flat={flat}
        />
    ),
);

const styles = StyleSheet.create({
    separator: {
        width: '100%',
        height: getSpacing(contentStackSpacing),
    },
    list: {
        flex: 1,
        width: '100%',
    },
    contentContainer: {
        padding: mainContentPadding,
    },
});

const LogList: React.FC<LogListProps> = ({
    profiles,
    isLoadingProfiles,
    selectedProfileIds,
    onToggleProfileId,
    onSetProfileIds,
    disableSelection,
    onLogPress,
    getPreLogListCards,
    auditLogTypes,
    searchTextPlaceholder,
    noDataEmptyStateVariant,
    searchEmptyStateVariant,
    flat,
}) => {
    const { isLoading, auditLogs } = useAuditLogs({
        medicalProfileIds: selectedProfileIds,
        auditLogTypes: auditLogTypes || homebaseAuditLogTypes,
    });
    const [searchText, setSearchText] = useState('');
    const filteredLogs = useMemo(() => {
        if (!searchText || !auditLogs?.length) return auditLogs;

        return applySearchFilter(auditLogs, searchText);
    }, [searchText, auditLogs]);
    const preCards = useMemo<PreLogCard[]>(() => {
        if (!getPreLogListCards) return [];

        return getPreLogListCards(selectedProfileIds).map((_) => ({ type: 'pre-log-card', card: _, key: uuid() }));
    }, [selectedProfileIds, getPreLogListCards]);

    const displayedCards = useMemo(() => [...preCards, ...filteredLogs], [preCards, filteredLogs]);

    const renderItem = useCallback<ListRenderItem<PreLogCard | AuditLogListItem>>(
        ({ item }) => {
            return <FlatListAuditLogItem auditLog={item} onLogPress={onLogPress} flat={flat} />;
        },
        [onLogPress, flat],
    );

    const ItemSeperatorComponent = useCallback(() => <View style={styles.separator} />, []);

    const keyExtractor = useCallback((item: PreLogCard | AuditLogListItem) => {
        return isPreLogCard(item) ? item.key : item.auditLogId;
    }, []);

    const renderListContent = useCallback(() => {
        if (selectedProfileIds.length === 0 && !displayedCards.length) {
            return <EmptyStateHint variant="audit-logs-profile-selection" />;
        }

        if (displayedCards.length === 0) {
            return <EmptyStateHint variant={searchEmptyStateVariant || 'audit-log-filter'} />;
        }

        if (auditLogs.length === 0 && !preCards.length) {
            return <EmptyStateHint variant={noDataEmptyStateVariant || 'audit-logs'} />;
        }

        return (
            <PRFlatList
                style={styles.list}
                data={displayedCards}
                ItemSeparatorComponent={ItemSeperatorComponent}
                contentContainerStyle={styles.contentContainer}
                renderItem={renderItem}
                keyExtractor={keyExtractor}
                initialNumToRender={Platform.OS === 'web' ? 20 : 10}
                maxToRenderPerBatch={Platform.OS === 'web' ? 20 : 10}
                windowSize={Platform.OS === 'web' ? 20 : 5}
                removeClippedSubviews={true}
            />
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedProfileIds, displayedCards, preCards, auditLogs, renderItem]);

    return (
        <AnimateLoadedContent isLoading={isLoadingProfiles}>
            {() => (
                <Stack vertical flex={1} fullWidth fullHeight>
                    <HeaderContainer>
                        <LogSearch
                            searchText={searchText}
                            onChange={setSearchText}
                            selectedProfileIds={selectedProfileIds}
                            onToggleProfileId={onToggleProfileId}
                            onSetProfileIds={onSetProfileIds}
                            isLoadingProfiles={isLoadingProfiles}
                            profiles={profiles}
                            disableSelection={disableSelection}
                            searchTextPlaceholder={searchTextPlaceholder}
                        />
                    </HeaderContainer>
                    <Stack vertical fullWidth flex={1}>
                        <AnimateLoadedContent isLoading={isLoading}>{renderListContent}</AnimateLoadedContent>
                    </Stack>
                </Stack>
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(LogList);
